import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, type FormEvent } from "react";
import { ArrowLeft, Mail, MessageSquare, LifeBuoy, ChevronDown, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { LiveChatWidget } from "@/components/LiveChatWidget";

const SUPPORT_EMAIL = "info@inquira.online";

export const Route = createFileRoute("/support")({
  component: SupportPage,
  head: () => ({
    meta: [
      { title: "Inquira Support" },
      { name: "description", content: "Inquira: AI Tutor support — get help with your account, subscriptions, billing, scanning, or study tools. Email info@inquira.online." },
      { property: "og:title", content: "Inquira Support" },
      { property: "og:description", content: "Browse FAQs or reach the Inquira team at info@inquira.online." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/support" }],
  }),
});

type FaqCategory = "billing" | "features" | "account" | "troubleshooting";

const FAQS: { q: string; a: string; c: FaqCategory }[] = [
  {
    c: "account",
    q: "How do I create an Inquira account?",
    a: "Tap Sign in on the Inquira home screen and choose Continue with Google or sign up with your email address and a password. Your study history, streak, and subscription follow your account on every device.",
  },
  {
    c: "features",
    q: "What is included with Inquira Premium?",
    a: "New Premium terms provide 50 AI Tutor Questions Every Day + Full Premium Study Access. These limits are staged, not active; existing benefits are preserved pending review. Premium includes guided explanations, quizzes, flashcards, planning, voice and supported subjects.",
  },
  {
    c: "billing",
    q: "How much does Inquira Premium cost?",
    a: "Premium Monthly is $9.99/month. The proposed $79.99/year plan saves approximately 33% compared with paying monthly and is not available until configured and verified. Existing subscription prices remain unchanged.",
  },
  {
    c: "billing",
    q: "How do I upgrade to Premium?",
    a: "Open the Plans page, pick Monthly or Yearly, and tap Upgrade. In the iOS app the purchase is completed through your Apple Account; on the web it is completed through our secure card checkout.",
  },
  {
    c: "billing",
    q: "How do I restore my purchase?",
    a: "If you already subscribed in the iOS app, sign in with the same Inquira account and tap Restore Purchases on the Plans or Subscription screen. Inquira re-verifies your receipt with the App Store and re-enables Premium.",
  },
  {
    c: "troubleshooting",
    q: "Why isn't my Premium subscription showing?",
    a: "First make sure you are signed in to the account you subscribed with. On iOS, tap Restore Purchases. On the web, open Subscription to refresh your status. If Premium still does not appear, email us and include the account email and purchase date.",
  },
  {
    c: "features",
    q: "What languages does Inquira support?",
    a: "Inquira supports 70+ languages, including right-to-left languages. Change your language from the selector in the header at any time.",
  },
  {
    c: "account",
    q: "How do I contact Inquira support?",
    a: `Email ${SUPPORT_EMAIL} or use the contact form on this page. We usually reply within 1–2 business days.`,
  },
  {
    c: "billing",
    q: "How many free scans do I get per day?",
    a: "Current Free access includes three scans per UTC day. The staged Free Forever plan provides three successful tutor questions per UTC day and one complimentary homework scan for a new account, without a card or subscription. Allowances reset at midnight UTC.",
  },
  {
    c: "features",
    q: "What does Inquira Pro include?",
    a: "Premium includes guided tutoring, AI voice explanations, practice, flashcards, quizzes and planning. New terms provide 50 questions per UTC day; existing benefits stay unchanged pending review and activation.",
  },
  {
    c: "billing",
    q: "Is there a yearly plan?",
    a: "The new $79.99 annual plan is being prepared and is not available yet. Existing annual subscriptions retain their current terms and renewal prices.",
  },
  {
    c: "billing",
    q: "How do I cancel my subscription?",
    a: "If you subscribed on the web, open Subscription and click Manage Subscription to cancel in the secure billing portal. If you subscribed through the Inquira iOS app, your subscription is billed by Apple and is managed in your Apple Account (Settings › your name › Subscriptions) — Inquira cannot cancel an Apple subscription for you. Either way, Premium stays active until the end of the current billing period.",
  },
  {
    c: "troubleshooting",
    q: "Voice explanations aren't playing. What should I try?",
    a: "Make sure your device isn't muted, that you're on a Pro plan, and that your browser allows audio autoplay for Inquira. If it still fails, email us the problem link and we'll investigate.",
  },
  {
    c: "troubleshooting",
    q: "I'm not receiving study reminders.",
    a: "Open Settings → Notifications and re-enable reminders. Your browser also needs notification permission for inquira.online. On iOS you must add Inquira to your Home Screen first.",
  },
  {
    c: "billing",
    q: "How do I request a refund?",
    a: `Email ${SUPPORT_EMAIL} within 7 days of a charge with your account email and a short note. We consider refunds case-by-case and honor statutory rights where applicable.`,
  },
  {
    c: "billing",
    q: "How do I cancel Inquira Premium?",
    a: "You can cancel your subscription anytime. If you subscribed through the Inquira iOS app, you can manage or cancel your subscription through your Apple Account subscription settings. Open Inquira, go to Profile > Subscription > Manage or Cancel Subscription.",
  },
  {
    c: "billing",
    q: "Will I be charged if I cancel during my free trial?",
    a: "Cancel before your free trial ends to avoid the first monthly subscription charge. Your subscription and trial access are subject to the billing platform's subscription terms.",
  },
  {
    c: "billing",
    q: "Can I continue using Premium after canceling?",
    a: "For a paid subscription, Premium access generally remains available until the end of the current paid billing period. The verified subscription status from the billing platform determines access.",
  },
  {
    c: "billing",
    q: "Do I get the 3-day free trial on the yearly plan?",
    a: "The 3-day free trial is an introductory offer on Inquira Premium Monthly. Pro Yearly is $99.99/year and does not include the monthly introductory trial. In the iOS app, Apple determines whether your Apple Account is eligible for the introductory offer — if it isn't, you'll see the standard $9.99/month subscription instead.",
  },
  {
    c: "account",
    q: "How do I delete my account?",
    a: `Email ${SUPPORT_EMAIL} from the address on your account and we'll delete your data within 30 days.`,
  },
  {
    c: "billing",
    q: "How do I earn a free month?",
    a: "Share your unique Inquira referral link and create an original TikTok about Inquira. When an eligible new customer uses your referral link and becomes a qualifying paid Premium subscriber, you can earn one free month of Inquira Premium.",
  },
  {
    c: "billing",
    q: "When does my referral count?",
    a: "A referral becomes eligible after the referred customer meets the program requirements and their qualifying paid subscription is verified by our billing systems.",
  },
  {
    c: "billing",
    q: "Does a free trial count as a referral?",
    a: "Starting a free trial alone does not complete a qualifying referral. The referred customer must become a qualifying paid subscriber.",
  },
  {
    c: "billing",
    q: "Can I earn multiple free months?",
    a: "Yes. Each separate qualifying referral may earn an additional free month, subject to the Referral Program Terms.",
  },
  {
    c: "account",
    q: "Where can I see my referrals?",
    a: "Go to Profile → Earn Free Months (the Invite friends page) to see your link, referral counts, and free months earned or redeemed.",
  },
];

const CATEGORIES: { id: FaqCategory | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "billing", label: "Billing" },
  { id: "features", label: "Features" },
  { id: "account", label: "Account" },
  { id: "troubleshooting", label: "Troubleshooting" },
];

/** Support categories shown as cards; each preselects the form category + FAQ filter. */
const SUPPORT_TOPICS: {
  title: string;
  blurb: string;
  formCategory: string;
  faq: FaqCategory | "all";
}[] = [
  { title: "Account & Login", blurb: "Sign in, email, password", formCategory: "Account", faq: "account" },
  { title: "Premium Subscriptions", blurb: "Plans, trials, entitlements", formCategory: "Subscription", faq: "billing" },
  { title: "Billing & Payments", blurb: "Charges, receipts, refunds", formCategory: "Billing", faq: "billing" },
  { title: "Scanning Questions", blurb: "Photos, crop, handwriting", formCategory: "Scanning", faq: "troubleshooting" },
  { title: "AI Tutor", blurb: "Explanations and follow-ups", formCategory: "AI Tutor", faq: "features" },
  { title: "Practice Mode", blurb: "Similar problems and drills", formCategory: "Practice Mode", faq: "features" },
  { title: "Flashcards & Quizzes", blurb: "Decks, reviews, scoring", formCategory: "Flashcards & Quizzes", faq: "features" },
  { title: "Technical Issues", blurb: "Errors, audio, notifications", formCategory: "Technical Issue", faq: "troubleshooting" },
  { title: "Privacy & Account Data", blurb: "Data export and deletion", formCategory: "Other", faq: "account" },
];

const FORM_CATEGORIES = [
  "Account",
  "Subscription",
  "Billing",
  "Technical Issue",
  "AI Tutor",
  "Scanning",
  "Practice Mode",
  "Flashcards & Quizzes",
  "Other",
] as const;

function SupportPage() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email ?? "");
  const [formCategory, setFormCategory] = useState<string>("Account");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<FaqCategory | "all">("all");

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      if (cat !== "all" && f.c !== cat) return false;
      if (!q) return true;
      return f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
    });
  }, [query, cat]);

  const mailtoHref = (() => {
    const body = `Name: ${name || "—"}\nEmail: ${email || "—"}\nCategory: ${formCategory}\n\n${message}\n\n— sent from Inquira (${window.location.origin})`;
    const params = new URLSearchParams({
      subject: subject || `Inquira support request — ${formCategory}`,
      body,
    });
    return `mailto:${SUPPORT_EMAIL}?${params.toString()}`;
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Please tell us your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) next.email = "Enter a valid email address so we can reply.";
    if (!subject.trim()) next.subject = "Add a short subject.";
    if (message.trim().length < 10) next.message = "Please describe your issue in at least 10 characters.";
    setErrors(next);
    if (Object.keys(next).length > 0) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      if (user) {
        const { error } = await supabase.from("feedback").insert({
          user_id: user.id,
          type: "bug",
          // Support requests are stored in the existing `feedback` table (RLS: owner insert only).
          // Outbound email delivery is intentionally handed off to the user's mail client until a
          // transactional email sender is configured server-side.
          message: `[Support form] ${subject.trim()} | category: ${formCategory} | from: ${name.trim()} <${email.trim()}>\n\n${message.trim()}`.slice(0, 4000),
          page_url: "/support",
        });
        if (error) throw error;
      }
      // Always offer the email hand-off so it reaches info@inquira.online.
      window.location.href = mailtoHref();
      setSent(true);
      toast.success("Opening your email app to send to " + SUPPORT_EMAIL);
    } catch {
      toast.error(`Could not save the ticket. You can email ${SUPPORT_EMAIL} directly.`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
      <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Home
      </Link>

      <div className="mb-8 flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <LifeBuoy className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-neon-pink">Inquira: AI Tutor</p>
          <h1 className="text-3xl font-bold tracking-tight">How can we help?</h1>
          <p className="mt-1 text-ink-muted">
            Get help with your Inquira account, subscriptions, billing, or study tools. Reach the team at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-primary underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>

      <section aria-labelledby="topics" className="mb-10">
        <h2 id="topics" className="mb-3 text-xl font-semibold">Browse by topic</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {SUPPORT_TOPICS.map((topic) => (
            <a
              key={topic.title}
              href="#contact"
              onClick={() => { setFormCategory(topic.formCategory); setCat(topic.faq); }}
              className="rounded-2xl border border-rule bg-card p-4 hover:bg-rule/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              <div className="font-semibold">{topic.title}</div>
              <div className="text-sm text-ink-muted">{topic.blurb}</div>
            </a>
          ))}
        </div>
      </section>

      <section className="mb-10 grid gap-3 sm:grid-cols-2">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex items-center gap-3 rounded-2xl border border-rule bg-card p-4 hover:bg-rule/20"
        >
          <Mail className="h-5 w-5 text-primary" />
          <div>
            <div className="font-semibold">Email Support</div>
            <div className="text-sm text-ink-muted">{SUPPORT_EMAIL}</div>
          </div>
        </a>
        <a
          href="#contact"
          className="flex items-center gap-3 rounded-2xl border border-rule bg-card p-4 hover:bg-rule/20"
        >
          <MessageSquare className="h-5 w-5 text-primary" />
          <div>
            <div className="font-semibold">Send a message</div>
            <div className="text-sm text-ink-muted">Uses the contact form below</div>
          </div>
        </a>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 text-xl font-semibold">Frequently asked questions</h2>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs…"
              className="w-full rounded-lg border border-rule bg-background py-2 pl-9 pr-3 text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                className={`rounded-lg border px-3 py-1.5 text-xs ${
                  cat === c.id ? "border-primary bg-primary/10" : "border-rule"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-rule overflow-hidden rounded-2xl border border-rule bg-card">
          {filteredFaqs.map((f) => (
            <details key={f.q} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
                <span className="font-medium">{f.q}</span>
                <span className="ml-auto mr-2 hidden text-[10px] uppercase tracking-wide text-ink-muted sm:inline">
                  {f.c}
                </span>
                <ChevronDown className="h-4 w-4 text-ink-muted transition-transform group-open:rotate-180" />
              </summary>
              <p className="whitespace-pre-line px-4 pb-4 text-sm text-ink-muted">{f.a}</p>
            </details>
          ))}
          {filteredFaqs.length === 0 && (
            <p className="p-4 text-sm text-ink-muted">
              No results. Try a different search or use the live chat below.
            </p>
          )}
        </div>
      </section>

      <section id="contact" className="mb-16">
        <h2 className="mb-1 text-xl font-semibold">Contact support</h2>
        <p className="mb-4 text-sm text-ink-muted">
          Send us a message and we'll reply from {SUPPORT_EMAIL}, usually within 1–2 business days.
        </p>
        <form onSubmit={submit} className="space-y-3 rounded-2xl border border-rule bg-card p-4">
          <div>
            <label htmlFor="sup-name" className="mb-1 block text-sm font-medium">Your name</label>
            <input
              id="sup-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "sup-name-err" : undefined}
              className="w-full rounded-lg border border-rule bg-background p-2.5 text-sm"
            />
            {errors.name && <p id="sup-name-err" className="mt-1 text-xs text-destructive">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="sup-email" className="mb-1 block text-sm font-medium">Your email</label>
            <input
              id="sup-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "sup-email-err" : undefined}
              className="w-full rounded-lg border border-rule bg-background p-2.5 text-sm"
            />
            {errors.email && <p id="sup-email-err" className="mt-1 text-xs text-destructive">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="sup-subject" className="mb-1 block text-sm font-medium">Subject</label>
            <input
              id="sup-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={140}
              placeholder="Short summary"
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? "sup-subject-err" : undefined}
              className="w-full rounded-lg border border-rule bg-background p-2.5 text-sm"
            />
            {errors.subject && <p id="sup-subject-err" className="mt-1 text-xs text-destructive">{errors.subject}</p>}
          </div>
          <div>
            <label htmlFor="sup-category" className="mb-1 block text-sm font-medium">Category</label>
            <select
              id="sup-category"
              value={formCategory}
              onChange={(e) => setFormCategory(e.target.value)}
              className="w-full rounded-lg border border-rule bg-background p-2.5 text-sm"
            >
              {FORM_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sup-message" className="mb-1 block text-sm font-medium">Message</label>
            <textarea
              id="sup-message"
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={4000}
              rows={6}
              placeholder="Describe your issue, feature request, or question."
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? "sup-message-err" : undefined}
              className="w-full rounded-lg border border-rule bg-background p-2.5 text-sm"
            />
            {errors.message && <p id="sup-message-err" className="mt-1 text-xs text-destructive">{errors.message}</p>}
          </div>
          {sent && (
            <p role="status" className="rounded-lg border border-primary/40 bg-primary/10 p-2.5 text-sm">
              Thanks — your request was recorded and your email app opened a message to {SUPPORT_EMAIL}.
            </p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="rounded-lg border border-rule px-4 py-2 text-sm hover:bg-rule/20"
            >
              Email directly
            </a>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send to support"}
            </button>
          </div>
        </form>
      </section>

      <section className="mb-16 rounded-2xl border border-rule bg-card p-4 text-sm text-ink-muted">
        <p className="font-semibold text-ink">Inquira: AI Tutor</p>
        <p className="mt-1">
          Subscriptions purchased in the iOS app are billed by Apple and managed in your Apple Account
          subscription settings.
        </p>
        <p className="mt-2 flex flex-wrap gap-3">
          <Link to="/pricing" className="font-semibold text-primary underline">Plans & Pricing</Link>
          <Link to="/privacy" className="font-semibold text-primary underline">Privacy Policy</Link>
          <Link to="/terms" className="font-semibold text-primary underline">Terms of Service</Link>
        </p>
      </section>
      <LiveChatWidget />
    </div>
  );
}